/*
  Orvani Mix & Match savings tracker (v2.1)

  CART context: every figure is rendered server-side in Liquid (snippets/
  mix-match-tracker.liquid) and is therefore always correct for the rendered cart.
  This script only reads `data-cart-qty` / `data-grand` and ANIMATES the Total roll
  and the progress-bar fill. It never fetches or computes the cart totals, so there
  are no races and the Total can never be stale.

  PRODUCT context: the widget persists (the page isn't re-rendered on cart change),
  so it reads /cart.js on cart mutations and shows the savings headline.
*/
(function () {
  // One global cart-change broadcaster — only needed by the product-page widget.
  if (!window.__mmtCartHook) {
    window.__mmtCartHook = true;
    var CART_MUTATORS = ['/cart/add', '/cart/change', '/cart/update', '/cart/clear'];
    var origFetch = window.fetch;
    window.fetch = function () {
      var url = arguments[0];
      var href = typeof url === 'string' ? url : (url && url.url) || '';
      var isMutator = CART_MUTATORS.some(function (p) { return href.indexOf(p) !== -1; });
      var result = origFetch.apply(this, arguments);
      if (isMutator) {
        result.then(function () { document.dispatchEvent(new CustomEvent('mmt:cart-changed')); }).catch(function () {});
      }
      return result;
    };
  }

  // The drawer / cart page rebuild the whole widget on every change. These hold the
  // last DISPLAYED Total + fill % (per location) so the rebuilt widget continues the
  // animation from where it was instead of snapping. This is ONLY for animation — the
  // numbers themselves always come from the server-rendered values.
  var mmtCartAnim = { drawer: {}, page: {} };

  class MixMatchTracker extends HTMLElement {
    connectedCallback() {
      this.tiers = (this.dataset.tiers || '')
        .split(',')
        .map(function (s) { return s.split(':'); })
        .filter(function (p) { return p.length === 2; })
        .map(function (p) { return { qty: parseInt(p[0], 10), pct: parseFloat(p[1]) }; })
        .sort(function (a, b) { return a.qty - b.qty; });
      if (!this.tiers.length) return;

      this.currency = this.dataset.currency || 'GBP';
      this.context = this.dataset.context || 'product';
      this.cartLoc = this.closest('cart-drawer') ? 'drawer' : 'page';
      this.reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this._lastSavings = 0;
      this._lastCart = null;
      this._productCache = {};
      this.els = {
        headline: this.querySelector('[data-mmt-headline]'),
        nudge: this.querySelector('[data-mmt-nudge]'),
        track: this.querySelector('[data-mmt-track]'),
        fill: this.querySelector('[data-mmt-fill]'),
        nodes: Array.prototype.slice.call(this.querySelectorAll('.mmt__node')),
        tGrand: this.querySelector('[data-mmt-grand]'),
      };
      this._tierCenters = this.computeCenters(85);

      // Measure-and-place the tier pills (both modes).
      this._onResize = this.layoutTiers.bind(this);
      window.addEventListener('resize', this._onResize);
      requestAnimationFrame(this._onResize);
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(this._onResize).catch(function () {});
      }

      if (this.context === 'cart') {
        // Server-rendered values are the source of truth — no fetching.
        this.applyCart();
      } else {
        this._onCartChanged = this.refresh.bind(this);
        document.addEventListener('mmt:cart-changed', this._onCartChanged);
        this._onVisibility = function () { if (!document.hidden) this.refresh(); }.bind(this);
        document.addEventListener('visibilitychange', this._onVisibility);
        window.addEventListener('pageshow', this._onCartChanged);
        this.refresh();
      }
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this._onResize);
      if (this._onCartChanged) {
        document.removeEventListener('mmt:cart-changed', this._onCartChanged);
        window.removeEventListener('pageshow', this._onCartChanged);
      }
      if (this._onVisibility) document.removeEventListener('visibilitychange', this._onVisibility);
    }

    // Even pill-to-pill spacing ending at the flush-right pill's centre (cLast).
    computeCenters(cLast) {
      var n = this.tiers.length;
      var g = cLast / (n - 0.5);
      var arr = [];
      for (var i = 0; i < n; i++) arr.push(g / 2 + i * g);
      arr[n - 1] = cLast;
      return arr;
    }

    // Measure the last pill, place node positions, then re-apply the fill.
    layoutTiers() {
      var nodes = this.els.nodes;
      var n = nodes.length;
      if (!n || !this.els.track) return;
      var trackW = this.els.track.offsetWidth;
      if (!trackW) return;
      var lastPill = nodes[n - 1].querySelector('.mmt__node-top');
      var lastPct = nodes[n - 1].querySelector('.mmt__node-bot');
      var halfPct = lastPill ? (lastPill.offsetWidth / 2 / trackW) * 100 : 12;
      var cLast = 100 - halfPct;
      this._tierCenters = this.computeCenters(cLast);
      for (var i = 0; i < n; i++) {
        if (i < n - 1) {
          nodes[i].style.left = this._tierCenters[i] + '%';
        } else {
          nodes[i].style.left = '100%';
          if (lastPill && lastPct) {
            lastPct.style.left = (-lastPill.offsetWidth / 2 - lastPct.offsetWidth / 2) + 'px';
            lastPct.style.right = 'auto';
            lastPct.style.transform = 'none';
          }
        }
      }
      // Re-apply the fill at the refined centres (no other state touched).
      if (this.context === 'cart') {
        this.setFill(this.computeFillPct(this.cartQty()));
      } else if (this._lastCart) {
        this.render(this._lastCart);
      }
    }

    money(cents) {
      try {
        return new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency: this.currency,
          currencyDisplay: 'narrowSymbol',
        }).format(cents / 100);
      } catch (e) {
        return (cents / 100).toFixed(2);
      }
    }

    fmtPct(p) {
      return (Math.round(p * 10) / 10) + '%';
    }

    // ---------- shared ladder (fill + pills) ----------

    computeFillPct(qty) {
      var tiers = this.tiers;
      var last = tiers[tiers.length - 1];
      var centers = this._tierCenters || this.computeCenters(85);
      if (qty <= 0) return 0;
      if (qty < tiers[0].qty) return (qty / tiers[0].qty) * centers[0];
      if (qty >= last.qty) return 100;
      var ti = tiers.findIndex(function (t) { return t.qty > qty; });
      var lowIdx = ti - 1;
      var span = tiers[ti].qty - tiers[lowIdx].qty;
      var into = (qty - tiers[lowIdx].qty) / span;
      return centers[lowIdx] + into * (centers[ti] - centers[lowIdx]);
    }

    // Cart mode: bridge from the last % across the DOM rebuild, then transition.
    // Product mode: a plain width change rides the CSS transition.
    setFill(pct) {
      var fillEl = this.els.fill;
      if (!fillEl) return;
      if (this.context === 'cart') {
        var anim = mmtCartAnim[this.cartLoc];
        var fromPct = anim.pct == null ? pct : anim.pct;
        fillEl.style.transition = 'none';
        fillEl.style.width = fromPct + '%';
        void fillEl.offsetWidth;
        fillEl.style.transition = '';
        fillEl.style.width = pct + '%';
        anim.pct = pct;
      } else {
        fillEl.style.width = pct + '%';
      }
      if (this.els.track) this.els.track.setAttribute('aria-valuenow', Math.round(pct));
    }

    // Fill + pill states for a given quantity. Returns the current tier (or null).
    renderLadder(qty) {
      var tiers = this.tiers;
      var current = null;
      for (var i = 0; i < tiers.length; i++) if (qty >= tiers[i].qty) current = tiers[i];

      this.setFill(this.computeFillPct(qty));

      var prevQty = mmtCartAnim[this.cartLoc].qty;
      var initialized = this._initialized;
      var cartMode = this.context === 'cart';
      var self = this;
      this.els.nodes.forEach(function (node) {
        var nodeQty = parseInt(node.dataset.qty, 10);
        var reached = qty >= nodeQty;
        var was = node.classList.contains('is-reached');
        node.classList.toggle('is-reached', reached);
        node.classList.toggle('is-current', current && current.qty === nodeQty);
        var newlyReached = cartMode
          ? (reached && prevQty != null && prevQty < nodeQty)
          : (reached && !was && initialized);
        if (newlyReached) self.pop(node.querySelector('.mmt__dot'));
      });
      this._initialized = true;
      return current;
    }

    pop(dot) {
      if (!dot || this.reduceMotion) return;
      dot.classList.remove('mmt--pop');
      void dot.offsetWidth;
      dot.classList.add('mmt--pop');
      dot.addEventListener('animationend', function () { dot.classList.remove('mmt--pop'); }, { once: true });
    }

    // ---------- cart mode (server-rendered totals; JS only animates) ----------

    cartQty() {
      return parseInt(this.dataset.cartQty || '0', 10);
    }

    applyCart() {
      var qty = this.cartQty();
      this.renderLadder(qty);
      var target = parseInt(this.dataset.grand || '0', 10);
      this.rollGrand(this.els.tGrand, mmtCartAnim[this.cartLoc], target);
      mmtCartAnim[this.cartLoc].qty = qty;
    }

    // Roll the Total from its live displayed value to `to` (cents). Tracks the shown
    // value + active rAF in shared state so it survives the rebuild, redirects mid-roll
    // (up/down), and a same-target re-apply doesn't restart it.
    rollGrand(el, anim, to) {
      if (!el) return;
      if (anim.grandRaf && anim.grandTarget === to) return;
      if (anim.grandRaf) { cancelAnimationFrame(anim.grandRaf); anim.grandRaf = null; }
      var from = anim.grandShown == null ? to : anim.grandShown;
      anim.grandTarget = to;
      anim.grandShown = from;
      el.textContent = this.money(from); // show the start value before the first paint
      if (from === to || this.reduceMotion) {
        anim.grandShown = to;
        el.textContent = this.money(to);
        return;
      }
      var self = this, dur = 550, start = null;
      var step = function (ts) {
        if (start === null) start = ts;
        var p = Math.min((ts - start) / dur, 1);
        var eased = 1 - Math.pow(1 - p, 3); // easeOutCubic
        var v = Math.round(from + (to - from) * eased);
        anim.grandShown = v;
        el.textContent = self.money(v);
        if (p < 1) {
          anim.grandRaf = requestAnimationFrame(step);
        } else {
          anim.grandRaf = null;
          anim.grandShown = to;
        }
      };
      anim.grandRaf = requestAnimationFrame(step);
    }

    // ---------- product mode (fetch + savings headline) ----------

    refresh() {
      var root = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/';
      fetch(root + 'cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(this.render.bind(this))
        .catch(function () {});
    }

    render(cart) {
      this._lastCart = cart;
      var qty = cart.item_count || 0;
      this.classList.toggle('mmt--empty', qty <= 0);

      var current = this.renderLadder(qty);

      // nudge copy
      var tiers = this.tiers;
      var first = tiers[0];
      var next = null;
      for (var i = 0; i < tiers.length; i++) { if (tiers[i].qty > qty) { next = tiers[i]; break; } }
      var nudge = this.els.nudge;
      if (nudge) {
        if (!current) {
          var needed = first.qty - qty;
          nudge.textContent = qty <= 0
            ? 'Add any ' + needed + ' items from the store to save ' + this.fmtPct(first.pct)
            : 'Add ' + needed + ' more ' + (needed === 1 ? 'item' : 'items') + ' to save ' + this.fmtPct(first.pct);
        } else if (next) {
          var more = next.qty - qty;
          nudge.textContent = 'Add ' + more + ' more ' + (more === 1 ? 'item' : 'items') + ' to save ' + this.fmtPct(next.pct) + ' off your entire order';
        } else {
          nudge.textContent = "You've unlocked our biggest saving";
        }
      }

      // savings headline = compare-at savings + mix & match (both as cash)
      var basis = cart.original_total_price || cart.total_price || 0;
      // floor (truncate) to match Lasso's checkout rounding — see mix-match-tracker.liquid
      var computed = current ? Math.floor(basis * current.pct / 100) : 0;
      var savings = cart.total_discount > 0 ? cart.total_discount : computed;
      var mixMatch = current ? savings : 0;
      var self = this;
      this.computeProductDiscount(cart).then(function (productSaving) {
        self.paintSavings(productSaving, mixMatch);
      });
    }

    paintSavings(productSaving, mixMatch) {
      var headline = this.els.headline;
      if (!headline) return;
      productSaving = productSaving || 0;
      mixMatch = mixMatch || 0;
      var total = productSaving + mixMatch;
      if (total <= 0) {
        headline.style.display = 'none';
        headline.innerHTML = '';
        this._lastSavings = 0;
        return;
      }
      headline.style.display = '';
      var from = this._lastSavings || 0;
      var amount = "<span class=\"mmt__amount\" data-mmt-amount>" + this.money(from) + '</span>';
      if (productSaving > 0 && mixMatch > 0) {
        headline.innerHTML = "You're saving " + amount +
          ' <span class="mmt__breakdown">(' + this.money(productSaving) + ' sale savings + ' +
          this.money(mixMatch) + ' mix &amp; match)</span>';
      } else if (mixMatch > 0) {
        headline.innerHTML = "You're saving " + amount + ' with Mix &amp; Match';
      } else {
        headline.innerHTML = "You're saving " + amount +
          ' <span class="mmt__breakdown">(sale savings)</span>';
      }
      this.countUp(headline.querySelector('[data-mmt-amount]'), from, total);
      this._lastSavings = total;
    }

    // Sum compare-at savings across the cart: (compare_at - selling) x qty, in cents.
    computeProductDiscount(cart) {
      var self = this;
      var items = (cart && cart.items) || [];
      return Promise.all(items.map(function (item) {
        return self.fetchProduct(item.handle).then(function (prod) {
          if (!prod || !prod.variants) return 0;
          var variant = prod.variants.filter(function (v) { return v.id === item.variant_id; })[0];
          if (!variant || !variant.compare_at_price) return 0;
          var diff = variant.compare_at_price - item.original_price;
          return diff > 0 ? diff * item.quantity : 0;
        }).catch(function () { return 0; });
      })).then(function (arr) {
        return arr.reduce(function (a, b) { return a + b; }, 0);
      });
    }

    fetchProduct(handle) {
      if (!handle) return Promise.resolve(null);
      if (this._productCache[handle]) return this._productCache[handle];
      var root = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/';
      var p = fetch(root + 'products/' + handle + '.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.ok ? r.json() : null; })
        .catch(function () { return null; });
      this._productCache[handle] = p;
      return p;
    }

    countUp(el, from, to) {
      if (!el) return;
      if (this._raf) cancelAnimationFrame(this._raf);
      if (from === to || this.reduceMotion) {
        el.textContent = this.money(to);
        return;
      }
      var self = this, dur = 550, start = null;
      var step = function (ts) {
        if (start === null) start = ts;
        var p = Math.min((ts - start) / dur, 1);
        var eased = 1 - Math.pow(1 - p, 3);
        el.textContent = self.money(Math.round(from + (to - from) * eased));
        if (p < 1) self._raf = requestAnimationFrame(step);
      };
      self._raf = requestAnimationFrame(step);
    }
  }

  if (!customElements.get('mix-match-tracker')) {
    customElements.define('mix-match-tracker', MixMatchTracker);
  }
})();
